import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  FileText, Users, Target, Dumbbell, Flag, Star,
  TrendingUp, TrendingDown, Loader2, ExternalLink,
  ClipboardList, BarChart3, Sparkles, Plus, X
} from 'lucide-react';
import { toast } from 'sonner';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';
import RichTextEditor from '../components/RichTextEditor';
import {
  SECTION_GENERATORS, generateSeasonHighlights,
} from '../utils/seasonReportSummaries';
import {
  SEASON_REPORT_SECTIONS, SECTION_BY_KEY,
  defaultSections, sectionStatus, recommendedSections,
  allOnSections, allOffSections,
} from '../utils/seasonReportSections';

const API = process.env.REACT_APP_BACKEND_URL;

const SEASONS = ['2025/2026', '2024/2025', '2023/2024'];

const SeasonReportBuilder = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('2025/2026');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAthletes, setLoadingAthletes] = useState(true);

  // Coach editable fields
  const [coachStrengths, setCoachStrengths] = useState('');
  const [coachImprovements, setCoachImprovements] = useState('');
  const [coachEvaluation, setCoachEvaluation] = useState('');
  const [coachRating, setCoachRating] = useState('');
  // Phase 77 — three new narrative fields (Executive Summary, Recommendations,
  // Next Season Action Plan). They live on `coach_input` so the saved
  // snapshot, the print template and RBAC paths all see them automatically.
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [nextSeasonPlan, setNextSeasonPlan] = useState('');

  // Phase 77 — section registry-driven toggles + per-section record counts
  // returned by GET /api/reports/season/{athlete_id}/counts. The 10-key
  // dict from Phase 76.1 is now superseded by the 18-key registry; legacy
  // saved snapshots are merged forward by mergeSavedSections() on load.
  const [sections, setSections] = useState(() => defaultSections());
  const [counts, setCounts] = useState({});       // { match_reports: 1, … }
  const [countsLoading, setCountsLoading] = useState(false);
  // Phase 76.3 — track the most recently saved report so we can show
  // permanent-link / view / finalize actions after Generate.
  const [savedReportId, setSavedReportId] = useState(null);

  // Phase 76.2 — editable staff summary per section + season highlights.
  const [sectionSummaries, setSectionSummaries] = useState({
    fitness: '', biometrics: '', nutrition: '',
    mental: '', academic: '', injury: '', career: '',
  });
  const [seasonHighlights, setSeasonHighlights] = useState([]);
  const [newHighlight, setNewHighlight] = useState('');

  const handleDraftSection = (key) => {
    const gen = SECTION_GENERATORS[key];
    const text = gen?.(reportData?.extended) || '';
    if (!text) {
      toast.error('Not enough data in this section to draft a summary yet.');
      return;
    }
    setSectionSummaries((s) => ({ ...s, [key]: text }));
    // Phase 76.2 — audit the draft generation event (fire-and-forget).
    fetch(`${API}/api/reports/season/event`, {
      method: 'POST', headers,
      body: JSON.stringify({
        athlete_id: selectedAthlete, season: selectedSeason,
        event: 'summary_generated', section: key,
      }),
    }).catch(() => {});
    toast.success('Draft summary inserted. Edit before saving.');
  };

  const handleDraftHighlights = () => {
    const items = generateSeasonHighlights(reportData);
    if (!items.length) {
      toast.error('Not enough season data to draft highlights yet.');
      return;
    }
    setSeasonHighlights(items);
    fetch(`${API}/api/reports/season/event`, {
      method: 'POST', headers,
      body: JSON.stringify({
        athlete_id: selectedAthlete, season: selectedSeason,
        event: 'summary_generated', section: 'highlights',
      }),
    }).catch(() => {});
    toast.success(`Drafted ${items.length} highlight${items.length === 1 ? '' : 's'}.`);
  };

  const addHighlight = () => {
    const v = newHighlight.trim();
    if (!v) return;
    setSeasonHighlights((h) => [...h, v]);
    setNewHighlight('');
  };
  const removeHighlight = (idx) =>
    setSeasonHighlights((h) => h.filter((_, i) => i !== idx));

  useEffect(() => {
    const fetchAthletes = async () => {
      try {
        const res = await fetch(`${API}/api/athletes`, { headers });
        if (res.ok) setAthletes(await res.json());
      } catch (err) { console.error(err); }
      finally { setLoadingAthletes(false); }
    };
    fetchAthletes();
  }, [token]);

  // Phase 76.3 fix — seed coach fields ONLY when the athlete/season pair
  // changes, never on a section toggle. Otherwise toggling a section
  // re-fetches and wipes any rich-text the staff member typed (bold,
  // links to highlight clips, etc.) into the editors.
  const seededKeyRef = useRef(null);

  const fetchReport = useCallback(async () => {
    if (!selectedAthlete || !selectedSeason) return;
    setLoading(true);
    try {
      // Phase 76.1 — include opt-in extended modules in the query.
      const qs = new URLSearchParams({ season: selectedSeason });
      if (sections.fitness)    qs.set('include_fitness', 'true');
      if (sections.biometrics) qs.set('include_biometrics', 'true');
      if (sections.nutrition)  qs.set('include_nutrition', 'true');
      if (sections.mental)     qs.set('include_mental', 'true');
      if (sections.academic)   qs.set('include_academic', 'true');
      if (sections.injury)     qs.set('include_injury', 'true');
      if (sections.career)     qs.set('include_career', 'true');
      const res = await fetch(
        `${API}/api/reports/season/${selectedAthlete}?${qs.toString()}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setReportData(data);
        // Pre-fill auto-generated suggestions only when this is the first
        // load for the (athlete, season) pair. Subsequent fetches (e.g.
        // section toggles) keep the user's typed rich text intact.
        const key = `${selectedAthlete}::${selectedSeason}`;
        if (seededKeyRef.current !== key) {
          seededKeyRef.current = key;
          setCoachStrengths(data.auto_strengths?.join(', ') || '');
          setCoachImprovements(data.auto_improvements?.join(', ') || '');
          if (data.latest_evaluation) {
            setCoachEvaluation(data.latest_evaluation.strengths || '');
          } else {
            setCoachEvaluation('');
          }
          setCoachRating(data.latest_evaluation_rating ? String(data.latest_evaluation_rating) : '');
        }
      } else {
        toast.error('Failed to load report data');
      }
    } catch { toast.error('Error fetching report data'); }
    finally { setLoading(false); }
  }, [selectedAthlete, selectedSeason, token, sections]);

  useEffect(() => { if (selectedAthlete) fetchReport(); }, [fetchReport, selectedAthlete, selectedSeason, sections]);

  // Phase 77 — fetch per-section record counts whenever the athlete/season
  // changes so the toggle grid shows live status (🟢 Good / 🟡 Limited /
  // 🔴 No Data) and the Recommended preset has data to work with.
  const fetchCounts = useCallback(async () => {
    if (!selectedAthlete || !selectedSeason) { setCounts({}); return; }
    setCountsLoading(true);
    try {
      const qs = new URLSearchParams({ season: selectedSeason });
      const res = await fetch(
        `${API}/api/reports/season/${selectedAthlete}/counts?${qs.toString()}`,
        { headers }
      );
      if (res.ok) {
        const body = await res.json();
        setCounts(body.counts || {});
      }
    } catch { /* counts are non-critical */ }
    finally { setCountsLoading(false); }
  }, [selectedAthlete, selectedSeason, token]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const toggleStatusCtx = {
    counts,
    narrativeText: {
      executive_summary: executiveSummary,
      evaluation: coachEvaluation,
      strengths: coachStrengths,
      improvements: coachImprovements,
      recommendations,
      next_season_plan: nextSeasonPlan,
    },
  };

  const applySelectAll = () => setSections(allOnSections());
  const applyDeselectAll = () => setSections(allOffSections());
  const applyRecommended = () => setSections(recommendedSections(toggleStatusCtx));

  const handleGenerate = async () => {
    if (!reportData) { toast.error('No data loaded'); return; }
    const printData = {
      ...reportData,
      coach_input: {
        strengths: coachStrengths,
        improvements: coachImprovements,
        evaluation: coachEvaluation,
        rating: coachRating ? parseFloat(coachRating) : null,
        // Phase 76.2 — persist staff summaries + highlights inside the
        // existing coach_input dict so saved/shared snapshots survive
        // reload and the print view can render them unchanged.
        section_summaries: sectionSummaries,
        season_highlights: seasonHighlights,
        // Phase 77 — three new narrative fields. Optional on legacy reads.
        executive_summary: executiveSummary,
        recommendations,
        next_season_plan: nextSeasonPlan,
      },
      generated_at: new Date().toISOString(),
    };

    // Save snapshot to database
    try {
      const res = await fetch(`${API}/api/reports/season/save`, {
        method: 'POST', headers,
        body: JSON.stringify({
          athlete_id: selectedAthlete,
          season: selectedSeason,
          // Phase 76.1 — persist `sections` inside the snapshot so saved /
          // shared reports keep the same extended-module visibility.
          snapshot: { ...reportData, sections },
          coach_input: printData.coach_input,
        }),
      });
      if (res.ok) {
        const saved = await res.json();
        printData.report_id = saved.id;
        setSavedReportId(saved.id);
        toast.success('Report saved to history');
      } else {
        // Phase 76.1 — surface save failure so coaches notice it (was silent before).
        toast.error('Could not save report snapshot — opening preview anyway');
      }
    } catch {
      toast.error('Could not save report snapshot — opening preview anyway');
    }

    // Phase 76.2 — fire a `season_report_exported` audit log (best effort).
    fetch(`${API}/api/reports/season/event`, {
      method: 'POST', headers,
      body: JSON.stringify({
        athlete_id: selectedAthlete, season: selectedSeason,
        event: 'exported', report_id: printData.report_id,
      }),
    }).catch(() => {});

    localStorage.setItem('pfap_season_report', JSON.stringify({
      ...printData,
      // Phase 76.1 — pass the section toggles to the print view so it can
      // selectively render the new modules.
      sections,
    }));
    window.open('/season-report/print', '_blank');
  };

  const d = reportData;
  const athlete = d?.athlete;

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 min-h-screen" data-testid="season-report-builder">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-heading uppercase text-pfap-green dark:text-gray-100" data-testid="season-report-heading">
            End of Season Report
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 font-body">
            Generate a comprehensive season report for any athlete
          </p>
        </div>

        {/* Selection */}
        <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700 mb-6" data-testid="report-selection">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 block">Athlete</label>
                <SearchableAthleteSelect
                  athletes={athletes}
                  value={selectedAthlete}
                  onChange={setSelectedAthlete}
                  placeholder={loadingAthletes ? 'Loading...' : 'Search & select athlete...'}
                  disabled={loadingAthletes}
                  data-testid="select-athlete"
                />
              </div>
              <div className="min-w-[160px]">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 block">Season</label>
                <Select value={selectedSeason} onValueChange={setSelectedSeason}>
                  <SelectTrigger className="w-full h-9 text-sm bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300" data-testid="select-season">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEASONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleGenerate}
                disabled={!reportData || loading}
                className="bg-pfap-green hover:bg-pfap-green/90 text-white h-9 px-5"
                data-testid="generate-report-btn"
              >
                <ExternalLink size={14} className="mr-1.5" /> Generate Report
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Phase 77 — Section control. All 18 sections are toggleable, each
            with a record-count badge + 🟢🟡🔴 status (data sections) or
            Filled / Empty (narrative). Toolbar offers Select All /
            Deselect All / Recommended Sections Only. */}
        {selectedAthlete && (
          <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700 mb-6" data-testid="section-toggles">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Include sections
                  {countsLoading && (
                    <span className="ml-2 text-[10px] text-gray-400 dark:text-gray-500 font-normal" data-testid="section-counts-loading">
                      analyzing data…
                    </span>
                  )}
                </CardTitle>
                <div className="flex items-center gap-1.5" data-testid="section-quick-actions">
                  <Button
                    size="sm" variant="outline"
                    className="h-7 text-[11px] px-2"
                    onClick={applySelectAll}
                    data-testid="section-select-all"
                  >Select All</Button>
                  <Button
                    size="sm" variant="outline"
                    className="h-7 text-[11px] px-2"
                    onClick={applyDeselectAll}
                    data-testid="section-deselect-all"
                  >Deselect All</Button>
                  <Button
                    size="sm"
                    className="h-7 text-[11px] px-2 bg-pfap-green hover:bg-pfap-green/90 text-white"
                    onClick={applyRecommended}
                    data-testid="section-recommended"
                  >Recommended</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                {SEASON_REPORT_SECTIONS.map((s) => {
                  const st = sectionStatus(s, toggleStatusCtx);
                  return (
                    <SectionToggleRow
                      key={s.key}
                      section={s}
                      checked={!!sections[s.key]}
                      onChange={() => setSections((curr) => ({ ...curr, [s.key]: !curr[s.key] }))}
                      status={st}
                    />
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-3">
                Sections with limited or no data are still selectable — useful when staff want to acknowledge the gap explicitly. A gentle warning appears next to each one.
              </p>
            </CardContent>
          </Card>
        )}

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-pfap-green" />
          </div>
        )}

        {!loading && !d && selectedAthlete && (
          <div className="text-center py-20">
            <FileText size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Select an athlete and season to preview the report</p>
          </div>
        )}

        {!loading && d && (
          <div className="space-y-4" data-testid="report-preview">
            {/* Phase 77 — Executive Summary (new narrative section). Shows
                a single rich-text editor at the top of the report when
                the toggle is on; the print template renders it before
                the athlete overview. */}
            <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Star size={16} className="text-pfap-green" /> Executive Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <RichTextEditor
                  value={executiveSummary}
                  onChange={setExecutiveSummary}
                  placeholder="One-paragraph overview of the athlete's season — typically read first by parents and recruiters."
                  testid="exec-summary"
                />
              </CardContent>
            </Card>

            {/* Athlete Overview */}
            <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Users size={16} className="text-pfap-green" /> Athlete Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  <InfoItem label="Name" value={athlete?.full_name} />
                  <InfoItem label="Position" value={athlete?.position} />
                  <InfoItem label="Squad" value={athlete?.squad} />
                  <InfoItem label="Nationality" value={athlete?.nationality} />
                  <InfoItem label="DOB" value={athlete?.date_of_birth} />
                  <InfoItem label="Season" value={d.season} />
                </div>
              </CardContent>
            </Card>

            {/* Performance Summary */}
            <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <BarChart3 size={16} className="text-blue-500" /> Performance Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-3">
                  <MetricCard label="Matches" value={d.performance?.total_matches ?? 0} />
                  <MetricCard label="Minutes" value={d.performance?.total_minutes ?? 0} />
                  <MetricCard label="Goals" value={d.performance?.goals ?? 0} />
                  <MetricCard label="Assists" value={d.performance?.assists ?? 0} />
                  <MetricCard label="Avg Rating" value={d.performance?.avg_rating ? `${d.performance.avg_rating}/10` : '-'} highlight />
                  <MetricCard label="Clean Sheets" value="-" />
                </div>
                {d.performance?.recent_matches?.length > 0 && (
                  <div className="border-t border-gray-100 dark:border-gray-700 pt-3 mt-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Recent Matches</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700">
                            <th className="pb-1.5 font-medium">Date</th>
                            <th className="pb-1.5 font-medium">Opponent</th>
                            <th className="pb-1.5 font-medium">Competition</th>
                            <th className="pb-1.5 font-medium">Pos</th>
                            <th className="pb-1.5 font-medium">Mins</th>
                            <th className="pb-1.5 font-medium">Rating</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-700 dark:text-gray-300">
                          {d.performance.recent_matches.map((m, i) => (
                            <tr key={i} className="border-b border-gray-50 dark:border-gray-700/50">
                              <td className="py-1.5">{m.date}</td>
                              <td className="py-1.5">{m.opponent}</td>
                              <td className="py-1.5">{m.competition || '-'}</td>
                              <td className="py-1.5">{m.position || '-'}</td>
                              <td className="py-1.5">{m.minutes || '-'}</td>
                              <td className="py-1.5">{m.rating ? <RatingBadge val={m.rating} /> : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* PDP Summary */}
            <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Target size={16} className="text-purple-500" /> PDP Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {d.pdp?.length > 0 ? d.pdp.map((cycle, i) => (
                  <div key={i} className={`${i > 0 ? 'border-t border-gray-100 dark:border-gray-700 pt-3 mt-3' : ''}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{cycle.cycle_name}</span>
                      <Badge className={`text-[10px] px-1.5 py-0 ${cycle.status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                        {cycle.status}
                      </Badge>
                      <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">{cycle.overall_progress}% overall</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                      <span className="text-gray-500 dark:text-gray-400">Goals: {cycle.total_goals}</span>
                      <span className="text-gray-500 dark:text-gray-400">Completed: {cycle.completed_goals}</span>
                    </div>
                    {Object.keys(cycle.categories || {}).length > 0 && (
                      <div className="mt-2 space-y-1">
                        {Object.entries(cycle.categories).map(([cat, goals]) => (
                          <div key={cat} className="flex items-center gap-2 text-xs">
                            <span className="text-gray-500 dark:text-gray-400 w-20 capitalize">{cat}</span>
                            <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${goals.reduce((s, g) => s + g.progress, 0) / goals.length}%` }} />
                            </div>
                            <span className="text-gray-400 dark:text-gray-500 w-8 text-right">{Math.round(goals.reduce((s, g) => s + g.progress, 0) / goals.length)}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )) : (
                  <p className="text-xs text-gray-400 dark:text-gray-500 py-3">No PDP data for this season</p>
                )}
              </CardContent>
            </Card>

            {/* Attendance */}
            <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Dumbbell size={16} className="text-emerald-500" /> Attendance Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="attendance-summary-grid">
                  <MetricCard
                    label="Academy Sessions in Period"
                    value={d.attendance?.academy_sessions_in_period ?? 0}
                  />
                  <MetricCard
                    label="Assigned / Invited"
                    value={d.attendance?.assigned_sessions ?? d.attendance?.total_sessions ?? 0}
                  />
                  <MetricCard
                    label="Attended"
                    value={d.attendance?.attended_or_justified ?? d.attendance?.sessions_attended ?? 0}
                  />
                  <MetricCard
                    label="Attendance %"
                    value={Number.isFinite(d.attendance?.attendance_rate ?? d.attendance?.attendance_pct) ? `${d.attendance?.attendance_rate ?? d.attendance?.attendance_pct}%` : '—'}
                    highlight
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3" data-testid="attendance-breakdown-grid">
                  <MetricCard
                    label="Not Attended"
                    value={d.attendance?.absent_or_no_show ?? d.attendance?.sessions_absent ?? 0}
                  />
                  <MetricCard
                    label="Pending / Unmarked"
                    value={d.attendance?.pending_or_unmarked ?? 0}
                  />
                  <MetricCard
                    label="Marked Sessions"
                    value={d.attendance?.marked_sessions ?? 0}
                  />
                  <MetricCard
                    label="Present"
                    value={d.attendance?.status_breakdown?.Present ?? 0}
                  />
                  <MetricCard
                    label="Full Training Availability"
                    value={Number.isFinite(d.attendance?.availability_rate) ? `${d.attendance.availability_rate}%` : '—'}
                  />
                </div>
                <p className="mt-3 text-[11px] text-gray-500 dark:text-gray-400">
                  Academy total ({d.attendance?.academy_sessions_in_period ?? 0}) shows all training sessions in the season.
                  Athlete-specific stats only count the {d.attendance?.assigned_sessions ?? 0} session(s) this athlete was assigned/invited to.
                  Pending entries are not counted as Absent.
                </p>
              </CardContent>
            </Card>

            {/* Flags / Discipline */}
            <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Flag size={16} className="text-red-500" /> Flags / Discipline
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {d.flags?.length > 0 ? (
                  <div className="space-y-2">
                    {d.flags.map((f, i) => {
                      const sevColor = f.severity === 'High' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : f.severity === 'Medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
                      return (
                        <div key={i} className="flex items-center gap-2 text-xs py-1.5 border-b border-gray-50 dark:border-gray-700/50 last:border-0">
                          <Badge className={`text-[10px] px-1.5 py-0 ${sevColor}`}>{f.severity}</Badge>
                          <span className="text-gray-700 dark:text-gray-300 font-medium">{f.flag_type}</span>
                          <span className="text-gray-400 dark:text-gray-500 flex-1 truncate">{f.description}</span>
                          <Badge className={`text-[10px] px-1.5 py-0 ${f.status === 'Resolved' ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'}`}>
                            {f.status}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-emerald-500 py-3">No flags recorded this season</p>
                )}
              </CardContent>
            </Card>

            {/* Phase 76.1 — Extended Modules preview (Batch A). Compact
                counts so staff can confirm data was pulled before generating. */}
            {d.extended && Object.keys(d.extended).length > 0 && (
              <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="extended-modules-preview">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <ClipboardList size={16} className="text-pfap-green" /> Extended Modules
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                    {d.extended.fitness_assessment && (
                      <ExtChip label="Fitness Assessments" value={`${d.extended.fitness_assessment.sessions_count} sessions`} testid="ext-fitness" />
                    )}
                    {d.extended.biometrics && (
                      <ExtChip label="Biometrics" value={`${d.extended.biometrics.entries_count} entries`} testid="ext-biometrics" />
                    )}
                    {d.extended.nutrition && (
                      <ExtChip label="Nutrition" value={`${d.extended.nutrition.plans_count} plans`} testid="ext-nutrition" />
                    )}
                    {d.extended.mental_hub && (
                      <ExtChip label="Mental Hub" value={`${d.extended.mental_hub.entries_count} entries · ${d.extended.mental_hub.completed_tasks} done`} testid="ext-mental" />
                    )}
                    {d.extended.academic && (
                      <ExtChip label="Academic" value={`${d.extended.academic.profiles?.length || 0} profile(s) · ${d.extended.academic.reports_count} reports`} testid="ext-academic" />
                    )}
                    {d.extended.injury_recovery && (
                      <ExtChip label="Injury & Recovery" value={`${d.extended.injury_recovery.injuries_count} injuries · ${d.extended.injury_recovery.active_count} active`} testid="ext-injury" />
                    )}
                    {d.extended.career_pathway && (
                      <ExtChip label="Career Pathway" value={`${d.extended.career_pathway.phases_count} phase(s)`} testid="ext-career" />
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Phase 76.2 — Season Highlights (editable list) */}
            <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="season-highlights-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Sparkles size={16} className="text-pfap-green" /> Season Highlights
                </CardTitle>
                <Button size="sm" variant="outline" onClick={handleDraftHighlights} data-testid="generate-highlights-btn">
                  <Sparkles size={12} className="mr-1" /> Generate Draft
                </Button>
              </CardHeader>
              <CardContent className="pt-0">
                {seasonHighlights.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="season-highlights-empty">
                    No highlights yet. Click <em>Generate Draft</em> or add your own below.
                  </p>
                ) : (
                  <ul className="space-y-1 mb-2" data-testid="season-highlights-list">
                    {seasonHighlights.map((h, i) => (
                      <li key={i} className="flex items-start justify-between gap-2 text-xs text-gray-700 dark:text-gray-200" data-testid={`season-highlight-${i}`}>
                        <span className="flex-1">• {h}</span>
                        <button onClick={() => removeHighlight(i)} className="text-gray-400 hover:text-red-600" data-testid={`remove-highlight-${i}`}>
                          <X size={12} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex gap-2 mt-2">
                  <Input
                    value={newHighlight}
                    onChange={(e) => setNewHighlight(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addHighlight(); } }}
                    placeholder="Add a custom highlight…"
                    className="text-xs"
                    data-testid="new-highlight-input"
                  />
                  <Button size="sm" variant="outline" onClick={addHighlight} data-testid="add-highlight-btn">
                    <Plus size={12} />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Phase 76.2 — Editable staff summary per extended section */}
            {Object.keys(sections).some((k) =>
              !['training_attendance','match_reports','pdp'].includes(k) && sections[k]) && (
              <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="section-summaries-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100">Section Staff Summaries</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  {[
                    ['fitness',    'Fitness Assessments'],
                    ['biometrics', 'Biometrics'],
                    ['nutrition',  'Nutrition'],
                    ['mental',     'Mental Hub'],
                    ['academic',   'Academic'],
                    ['injury',     'Injury & Recovery'],
                    ['career',     'Career Pathway'],
                  ].filter(([k]) => sections[k]).map(([key, label]) => (
                    <div key={key} className="border border-gray-100 dark:border-gray-700 rounded-md p-3" data-testid={`section-summary-${key}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">{label} — Staff Summary</p>
                        <Button size="sm" variant="ghost" className="text-xs h-6"
                                onClick={() => handleDraftSection(key)}
                                data-testid={`draft-summary-${key}-btn`}>
                          <Sparkles size={11} className="mr-1" /> Generate Draft
                        </Button>
                      </div>
                      <Textarea
                        rows={3}
                        value={sectionSummaries[key] || ''}
                        onChange={(e) => setSectionSummaries((s) => ({ ...s, [key]: e.target.value }))}
                        placeholder="Click Generate Draft or write your own summary…"
                        className="text-xs hidden"
                        data-testid={`section-summary-${key}-input-legacy`}
                      />
                      <RichTextEditor
                        value={sectionSummaries[key] || ''}
                        onChange={(html) => setSectionSummaries((s) => ({ ...s, [key]: html }))}
                        placeholder="Click Generate Draft or write your own summary. Add links to highlight clips with the link button."
                        testid={`section-summary-${key}-input`}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Coach Input: Strengths */}
            <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <TrendingUp size={16} className="text-emerald-500" /> Strengths
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {d.auto_strengths?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {d.auto_strengths.map((s, i) => (
                      <Badge key={i} className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/40">{s}</Badge>
                    ))}
                  </div>
                )}
                <RichTextEditor
                  value={coachStrengths}
                  onChange={setCoachStrengths}
                  placeholder="Add coach observations about the player's strengths. Use the link button for video evidence."
                  testid="coach-strengths"
                />
              </CardContent>
            </Card>

            {/* Coach Input: Areas for Improvement */}
            <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <TrendingDown size={16} className="text-amber-500" /> Areas for Improvement
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {d.auto_improvements?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {d.auto_improvements.map((s, i) => (
                      <Badge key={i} className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800/40">{s}</Badge>
                    ))}
                  </div>
                )}
                <RichTextEditor
                  value={coachImprovements}
                  onChange={setCoachImprovements}
                  placeholder="Add areas the player needs to improve. Attach video evidence via the link button (e.g., 'Decision-making in transition – Watch clip')."
                  testid="coach-improvements"
                />
              </CardContent>
            </Card>

            {/* Coach Evaluation */}
            <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <ClipboardList size={16} className="text-pfap-green" /> Coach Evaluation
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">Overall Rating (1-10)</label>
                  <Select value={coachRating} onValueChange={setCoachRating}>
                    <SelectTrigger className="w-32 h-9 text-sm bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300" data-testid="coach-rating">
                      <SelectValue placeholder="Rating" />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(n => (
                        <SelectItem key={n} value={String(n)}>{n}/10</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">Coach Comments</label>
                  <RichTextEditor
                    value={coachEvaluation}
                    onChange={setCoachEvaluation}
                    placeholder="Final evaluation and overall assessment of the player's season. Add video evidence with the link button."
                    testid="coach-evaluation"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Phase 77 — Recommendations + Next Season Action Plan
                (new narrative sections). Both stored on coach_input. */}
            <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Sparkles size={16} className="text-purple-500" /> Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <RichTextEditor
                  value={recommendations}
                  onChange={setRecommendations}
                  placeholder="Specific recommendations for the coming weeks (training focus, position trial, sleep/nutrition habits, etc.)."
                  testid="recommendations"
                />
              </CardContent>
            </Card>

            <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Flag size={16} className="text-blue-500" /> Next Season Action Plan
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <RichTextEditor
                  value={nextSeasonPlan}
                  onChange={setNextSeasonPlan}
                  placeholder="High-level goals for next season — pre-season targets, league/cup objectives, individual development priorities."
                  testid="next-season-plan"
                />
              </CardContent>
            </Card>

            {/* Generate Button */}
            <div className="flex justify-end pt-2 pb-8 flex-wrap gap-2">
              <Button
                onClick={handleGenerate}
                className="bg-pfap-green hover:bg-pfap-green/90 text-white h-10 px-6 text-sm"
                data-testid="generate-report-btn-bottom"
              >
                <ExternalLink size={16} className="mr-2" /> Generate Report in New Tab
              </Button>
            </div>

            {/* Phase 76.3 — Post-save action row. Visible after the report
                has been saved at least once; reused for finalise / archive
                / link copy via the dedicated PATCH endpoints. */}
            {savedReportId && (
              <div
                className="border border-gray-200 dark:border-gray-700 rounded-md p-3 bg-gray-50 dark:bg-gray-800/40 flex flex-wrap items-center gap-2 mb-8"
                data-testid="post-save-actions"
              >
                <span className="text-xs text-gray-600 dark:text-gray-300">Saved:</span>
                <Button
                  size="sm" variant="outline"
                  onClick={() => window.open(`/season-reports/saved/${savedReportId}`, '_blank')}
                  data-testid="view-saved-report-btn"
                >
                  View Saved Report
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={async () => {
                    const url = `${window.location.origin}/season-reports/saved/${savedReportId}`;
                    try {
                      await navigator.clipboard.writeText(url);
                      toast.success('Report link copied to clipboard');
                    } catch {
                      toast.message(url);
                    }
                    fetch(`${API}/api/reports/season/saved/${savedReportId}/link-copied`,
                      { method: 'POST', headers }).catch(() => {});
                  }}
                  data-testid="copy-report-link-btn"
                >
                  Copy Report Link
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => { setSavedReportId(null); toast.success('Continue editing — your next Generate will save a fresh version.'); }}
                  data-testid="continue-editing-btn"
                >
                  Continue Editing
                </Button>
                <Button
                  size="sm"
                  className="bg-pfap-green hover:bg-pfap-green/90 text-white"
                  onClick={async () => {
                    try {
                      const r = await fetch(`${API}/api/reports/season/saved/${savedReportId}/status`,
                        { method: 'PATCH', headers, body: JSON.stringify({ status: 'finalized' }) });
                      if (r.ok) toast.success('Report finalised — now visible to player/parent.');
                      else toast.error('Failed to finalise report');
                    } catch { toast.error('Failed to finalise report'); }
                  }}
                  data-testid="finalize-report-btn"
                >
                  Finalize Report
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

const InfoItem = ({ label, value }) => (
  <div>
    <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider">{label}</p>
    <p className="text-sm text-gray-800 dark:text-gray-200 font-medium">{value || '-'}</p>
  </div>
);

const MetricCard = ({ label, value, highlight }) => (
  <div className="text-center p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
    <p className={`text-lg font-bold ${highlight ? 'text-pfap-green dark:text-emerald-400' : 'text-gray-900 dark:text-gray-100'}`}>
      {value}
    </p>
    <p className="text-[10px] text-gray-400 dark:text-gray-500">{label}</p>
  </div>
);

const RatingBadge = ({ val }) => {
  const color = val >= 7.5 ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' : val >= 6 ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400';
  return <Badge className={`text-[10px] px-1.5 py-0 ${color}`}>{val}/10</Badge>;
};

// Phase 77 — section toggle row used inside the section-toggles card.
// Renders the checkbox + label, a record-count badge for data sections,
// a Filled/Empty pill for narrative sections, and a gentle warning when
// the section is INCLUDED yet has no/limited data (Part 5 of the spec).
const SectionToggleRow = ({ section, checked, onChange, status }) => {
  const toneCls = {
    good:    'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    limited: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    none:    'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    filled:  'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    empty:   'bg-gray-100 text-gray-500 dark:bg-gray-700/40 dark:text-gray-400',
  }[status.tone];
  const dot = {
    good: '🟢', limited: '🟡', none: '🔴', filled: '●', empty: '○',
  }[status.tone];
  // Warning shows only when staff TICKED a section that has no/limited
  // data (data) or empty text (narrative). Never blocks.
  const isProblem =
    checked && (status.tone === 'none' || status.tone === 'limited' || status.tone === 'empty');
  const warningCopy =
    section.kind === 'narrative'
      ? 'No staff text yet — section will print as "Not provided".'
      : status.tone === 'none'
        ? `${section.label} contains no season data and may reduce report quality.`
        : `${section.label} has limited data this season.`;
  return (
    <div
      className={`p-2 rounded-md border ${checked ? 'border-pfap-green/50 bg-pfap-green/5' : 'border-gray-200 dark:border-gray-700'}`}
      data-testid={`section-row-${section.key}`}
    >
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="accent-pfap-green"
          data-testid={`section-toggle-${section.key}`}
        />
        <span className="flex-1 text-xs text-gray-800 dark:text-gray-200">
          {section.label}
          {section.kind === 'data' && status.count !== null && (
            <span className="ml-1.5 text-[11px] text-gray-500 dark:text-gray-400" data-testid={`section-count-${section.key}`}>
              ({status.count})
            </span>
          )}
        </span>
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded-full ${toneCls}`}
          data-testid={`section-status-${section.key}`}
        >
          <span className="mr-1">{dot}</span>{status.label}
        </span>
      </label>
      {isProblem && (
        <p
          className="mt-1.5 ml-6 text-[10px] text-amber-600 dark:text-amber-400"
          data-testid={`section-warning-${section.key}`}
        >
          {warningCopy}
        </p>
      )}
    </div>
  );
};

// Phase 76.1 — small toggle chip used in the Module Sections card.
const ToggleChip = ({ label, checked, onChange, disabled, testid }) => (
  <label
    className={`flex items-center gap-2 px-3 py-2 rounded-md border text-xs cursor-pointer select-none transition ${
      checked
        ? 'border-pfap-green bg-pfap-green/5 text-pfap-green'
        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'
    } ${disabled ? 'opacity-90 cursor-default' : ''}`}
    data-testid={testid}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className="accent-pfap-green"
    />
    <span>{label}</span>
  </label>
);

// Phase 76.1 — compact preview chip for the Extended Modules block.
const ExtChip = ({ label, value, testid }) => (
  <div className="px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30" data-testid={testid}>
    <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider">{label}</p>
    <p className="text-xs text-gray-700 dark:text-gray-200 font-medium">{value}</p>
  </div>
);

export default SeasonReportBuilder;
